/**
 * WorkflowNavButtons - Shared Back/Continue button row used by all workflow steps.
 *
 * Handles two variants:
 * - Standard: outline Back button + green Continue/action button
 * - Export: two muted ActionButtons (used on the final Export step)
 */

import { memo } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router';
import { ActionButton } from './FlightPlannerStyles';

const buttonBaseStyle = {
	borderRadius: '10px',
	padding: '0.65rem 1rem',
	fontWeight: 600,
	fontSize: '0.9rem',
	flex: 1,
};

const actionOverrideStyle = {
	width: 'auto',
	flex: 1,
	marginTop: 0,
};

const mutedStyle = {
	background: 'rgba(255, 255, 255, 0.06)',
	border: '1px solid rgba(255, 255, 255, 0.12)',
};

/**
 * @param {object}   props
 * @param {string}   [props.backLabel='← Back']     - Label for the back button
 * @param {Function} [props.onBack]                 - onClick for back button (omit when using backTo)
 * @param {string}   [props.backTo]                 - react-router path; renders a Link instead of button
 * @param {string}   [props.continueLabel='Continue →'] - Label for the continue button
 * @param {Function} [props.onContinue]             - onClick for continue button
 * @param {boolean}  [props.continueDisabled=false] - Disables the continue button
 * @param {boolean}  [props.muted=false]            - Export-step style: both buttons muted, no green
 * @param {string}   [props.secondaryLabel]         - Second button label when muted=true
 * @param {Function} [props.onSecondary]            - Second button onClick when muted=true
 */
const WorkflowNavButtons = ({
	backLabel = '← Back',
	onBack,
	backTo,
	continueLabel = 'Continue →',
	onContinue,
	continueDisabled = false,
	muted = false,
	secondaryLabel,
	onSecondary,
}) => {
	if (muted) {
		// Export step: two equal muted ActionButtons
		return (
			<div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
				<ActionButton onClick={onBack} style={mutedStyle}>
					{backLabel}
				</ActionButton>
				{secondaryLabel && (
					<ActionButton onClick={onSecondary} style={mutedStyle}>
						{secondaryLabel}
					</ActionButton>
				)}
			</div>
		);
	}

	return (
		<div style={{ display: 'flex', gap: '0.5rem' }}>
			{backTo ? (
				<Button variant="outline-secondary" as={Link} to={backTo} style={buttonBaseStyle}>
					{backLabel}
				</Button>
			) : (
				<Button variant="outline-secondary" onClick={onBack} style={buttonBaseStyle}>
					{backLabel}
				</Button>
			)}
			<ActionButton
				variant="success"
				onClick={onContinue}
				disabled={continueDisabled}
				style={actionOverrideStyle}>
				{continueLabel}
			</ActionButton>
		</div>
	);
};

export default memo(WorkflowNavButtons);
